import { ChangeEvent, useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import IOnGetOrderDTO from "../../../models/DTO/IOnGetOrderDTO";
import EndpointsEnum from "../../../models/EndpointsEnum";
import ICategory from "../../../models/ICategory";
import IOrder from "../../../models/IOrder";
import OrderEntryService from "../../../services/OrderEntryService";
import UIService from "../../../services/UIService";
import OwInputWrapper from "../../shared/OwInputWrapper/OwInputWrapper";
import OwLabel from "../../shared/OwLabel/OwLabel";
import "./EcommerceSearchSubHeader.scss";
import { EcommerceCategorySearchSelection } from "../EcommerceCategorySearchSelection/EcommerceCategorySearchSelection";
import CategoryContext from "../../../contexts/CategoryContext";
import { Accordion, Button, Dropdown, InputGroup, SplitButton } from "react-bootstrap";
import SupplierGuidEnum from "../../../models/SupplierGuidEnum";
import SupplierContext from "../../../contexts/SupplierContext";
import UserContext from "../../../contexts/UserContext";
import VendorContext from "../../../contexts/VendorContext";
import IUiSocketSetup from "../../../models/IUiSocketSetup";
import { SocketContext } from "../../../contexts/SocketContext";
import UiContext from "../../../contexts/UiContext";
import { useCart } from "../../../contexts/CartContext";

type Props = {
    lookupLeft: string;
    lookupWidth: string;
    toggleCatalogSidebar: () => void;
    toggleCartSidebar: () => void;
    closeCartSidebar?: () => void;
    viewCart?: () => void;
    cartOpen?: boolean;
    isReadOnly: boolean;
    performSearch: (searchTerm: string, categoryGuid?: string, callingFn?: string) => void;
    isCheckoutView: boolean;
    order?: IOrder;
    getCommonItems: () => void;
    categories: ICategory[];
    mainUrl?: string;
    clearSearchTerm?: number;
    initialSearchTerm?: string;
};

export const EcommerceSearchSubHeader = (props: Props) => {
    const supplierModel = useContext(SupplierContext);
    const userModel = useContext(UserContext);
    const vendorModel = useContext(VendorContext);
    const categoryContext = useContext(CategoryContext);

    // Vendors locked to their default template items don't get to browse or search the catalog.
    const hideCatalogBrowsing =
        !userModel.isSupplierUser &&
        !!vendorModel?.is_ecomm_user &&
        !!vendorModel?.ecomm_template_items_only &&
        !!supplierModel.has_templates;
    const previousCategoryContext = useRef(categoryContext.category);
    const isInitialMount = useRef(true);
    const socket = useContext(SocketContext)!;
    const uiContext = useContext(UiContext);
    const { inactiveCarts, inactiveCartCount } = useCart();

    const [cartOpen, setCartOpen] = useState(props.cartOpen);
    const [categories, setCategories] = useState<ICategory[]>(props.categories);
    const [selectedCategory, setSelectedCategory] = useState<ICategory | undefined | null>();
    const [searchTerm, setSearchTerm] = useState(props.initialSearchTerm || "");
    const [lookupWidth, setLookupWidth] = useState(props.lookupWidth);
    const [lookupLeft, setLookupLeft] = useState(props.lookupLeft);
    const [cartCount, setCartCount] = useState(uiContext.cartItemCount || props.order?.Order_Items.length || 0);
    const [retainTerm, setRetainTerm] = useState(false);

    useEffect(() => {
        if (uiContext.cartItemCount >= 0 && cartCount !== uiContext.cartItemCount) {
            setCartCount(uiContext.cartItemCount);
        }
    }, [uiContext.cartItemCount, cartCount]);

    const navigate = useNavigate();

    const onGetOrder = (data: IOnGetOrderDTO) => {
        uiContext.setCartItemCount(data.order.Order_Items.length);
    };

    const sockets: IUiSocketSetup[] = [{ endpoint: EndpointsEnum.OrderOnGetOrder, fn: onGetOrder }];

    useEffect(() => {
        for (const _socket of sockets) {
            socket.on(_socket.endpoint, _socket.fn);
        }

        return () => {
            for (const _socket of sockets) {
                socket.off(_socket.endpoint, _socket.fn);
            }
        };
    }, []);

    useEffect(() => {
        // Skip clearing search term if we have an initialSearchTerm from URL and this is
        // the initial category being set (not a user-initiated category change)
        const isInitialCategorySetFromUrl = isInitialMount.current && props.initialSearchTerm;

        if (categoryContext.category && previousCategoryContext.current?.guid !== categoryContext.category?.guid) {
            previousCategoryContext.current = categoryContext.category;
            // Only clear search term if this is NOT the initial URL-based category setup
            if (!isInitialCategorySetFromUrl) {
                setSearchTerm("");
            }
            // Mark initial mount as complete only after we've processed a category being set
            if (isInitialMount.current) {
                isInitialMount.current = false;
            }
        } else if (!categoryContext.category && previousCategoryContext.current) {
            // Clear search term when category is cleared (e.g., clicking Clear Filters)
            previousCategoryContext.current = undefined;
            setSearchTerm("");
        }
    }, [categoryContext]);

    useEffect(() => {
        if (props.clearSearchTerm) {
            setSearchTerm("");
        }
    }, [props.clearSearchTerm]);

    useEffect(() => {
        setLookupLeft(props.lookupLeft);
    }, [props.lookupLeft]);

    useEffect(() => {
        setLookupWidth(props.lookupWidth);
    }, [props.lookupWidth]);

    useEffect(() => {
        const lookupSize = UIService.getLookupSize(uiContext.menuOpen, uiContext.menuIconMode);

        if (lookupSize.lookupLeft !== lookupLeft) {
            setLookupLeft(lookupSize.lookupLeft);
        }

        if (lookupSize.lookupWidth !== lookupWidth) {
            setLookupWidth(lookupSize.lookupWidth);
        }
    }, [uiContext.menuOpen, uiContext.menuIconMode]);

    const handleChange = (e: ChangeEvent<any>) => {
        const value = e.target.value;

        setSearchTerm(value);
    };

    const goToMain = () => {
        if (props.mainUrl) {
            navigate(props.mainUrl);
        } else {
            navigate({
                pathname: `/orders/${props.order!.guid}/ecom`,
            });
        }

        setSearchTerm("");
        setSelectedCategory(null);
        categoryContext.setFn(undefined);

        props.getCommonItems();
    };

    const performSearch = (callingFn?: string) => {
        if (!searchTerm.trim() && !categoryContext.category) {
            goToMain();
            return;
        }

        let categoryGuid = selectedCategory?.guid;

        if (categoryContext.category) {
            categoryGuid = categoryContext.category.guid;
        }

        props.performSearch(searchTerm, categoryGuid, callingFn || "ESSH - performSearch");
    };

    const enterSearch = (e: any) => {
        if (e.key === "Enter" || e.key === "enter") {
            performSearch("ESSH enterSearch");
            return;
        }
    };

    const setCategory = (category?: ICategory) => {
        setRetainTerm(true);
        setSelectedCategory(category);
    };

    const viewCart = () => {
        if (props.viewCart) {
            props.viewCart();
        }
    };

    const goToInactiveCarts = () => {
        if (inactiveCartCount === 1 && inactiveCarts[0]?.cartGuid) {
            navigate(`/ecomm/inactive-cart/${inactiveCarts[0].cartGuid}`);
        } else {
            navigate("/ecomm/orders");
        }
    };

    const handleCartClick = () => {
        if (cartOpen) {
            if (props.closeCartSidebar) {
                props.closeCartSidebar();
            } else {
                props.toggleCartSidebar();
            }

            setCartOpen(false);
        } else {
            props.toggleCartSidebar();
            setCartOpen(true);
        }
    };

    const handleQuickSearch = (item: string) => {
        setSearchTerm(item);
        props.performSearch(item, undefined, "ESSH - handleQuickSearch");
    };

    let quickSearchItems: { title: string; items: { type: string; value: string }[] }[] = [
        // { type: "header", value: "Pattern" },
        // { type: "term", value: "20x10" },
        // { type: "divider", value: "--" },
        // { type: "header", value: "Bolts" },
        // { type: "term", value: "5x115" },
        // { type: "term", value: "5x120" },
        // { type: "divider", value: "--" },
        // { type: "header", value: "Style" },
        // { type: "term", value: "Black" },
        // { type: "term", value: "Chrome" },
    ];

    if (supplierModel.guid === SupplierGuidEnum.TEXAS_TIRE) {
        quickSearchItems = [
            {
                title: "Bolt",
                items: [
                    { type: "term", value: "5x127/5x139" },
                    { type: "term", value: "5x139/5x150" },
                    { type: "term", value: "6x135/6x139" },
                    { type: "term", value: "8x165" },
                    { type: "term", value: "8x170" },
                    { type: "term", value: "8x180" },
                    { type: "term", value: "8x200" },
                    { type: "term", value: "8x210" },
                ],
            },
            {
                title: "Pattern (Off-Road)",
                items: [
                    { type: "term", value: "20x10" },
                    { type: "term", value: "22x12" },
                    { type: "term", value: "22x14" },
                    { type: "term", value: "24x12" },
                    { type: "term", value: "24x14" },
                    { type: "term", value: "26x12" },
                    { type: "term", value: "26x14" },
                ],
            },
            {
                title: "Pattern (Dually)",
                items: [
                    { type: "term", value: "20x8.5F" },
                    { type: "term", value: "20x8.5R" },
                    { type: "term", value: "20x8.5I" },
                ],
            },
            {
                title: "Pattern (Infamous)",
                items: [
                    { type: "term", value: "20x9" },
                    { type: "term", value: "20x11" },
                    { type: "term", value: "22x9" },
                    { type: "term", value: "22x11" },
                    { type: "term", value: "24x9" },
                    { type: "term", value: "24x11" },
                ],
            },
            {
                title: "Finish (Off-Road)",
                items: [
                    { type: "term", value: "Satin Black" },
                    { type: "term", value: "Gloss Black" },
                    { type: "term", value: "Gloss/Machine Face" },
                    { type: "term", value: "Chrome" },
                    { type: "term", value: "Satin Black/Milled" },
                    { type: "term", value: "Satin Black/Red Milled" },
                    { type: "term", value: "Gloss Black/Red Milled" },
                ],
            },
            {
                title: "Finish (Infamous)",
                items: [
                    { type: "term", value: "Chrome" },
                    { type: "term", value: "Polished" },
                    { type: "term", value: "Polished/Black Machine Face" },
                    { type: "term", value: "Polished/w Chrome Lip" },
                ],
            },
        ];
    }

    return (
        <div className={"EcommerceSearchSubHeader"} id={"EcommerceSearchSubHeader"}>
            <div className={"top-bar flex-column flex-md-row"} style={{ width: lookupWidth, left: lookupLeft }}>
                {props.isReadOnly ? null : (
                    <div className="d-flex d-md-none justify-content-between w-100">
                        {props.categories.length && !hideCatalogBrowsing ? (
                            <div className={"catalog-button d-flex align-items-center"} onClick={props.toggleCatalogSidebar}>
                                <i className={"fad fa-book-open fa-2x"} />
                                <div>
                                    <OwLabel title={"categories"} noBottomMargin={true} className={"ms-2"} />
                                </div>
                            </div>
                        ) : null}
                        <div className={"cart-detail ms-auto"}>
                            {props.isCheckoutView ? null : (
                                <>
                                    <OwLabel className={"cart"} title={"cart"} leftIconClass={"fad fa-shopping-cart"} fn={viewCart} />
                                    <div className={"cart-count-wrapper"}>
                                        <div className={"cart-count"}>{cartCount || props.order?.Order_Items.length || 0}</div>
                                    </div>
                                    {inactiveCartCount > 0 ? (
                                        <div className={"inactive-cart-badge"} title={"Carts awaiting payment"} onClick={goToInactiveCarts}>
                                            <i className={"fas fa-exclamation-triangle me-1"} />
                                            {inactiveCartCount}
                                        </div>
                                    ) : null}
                                </>
                            )}
                        </div>
                    </div>
                )}
                <div>
                    {props.isReadOnly ? null : (
                        <>
                            <div className={"d-flex align-items-end"}>
                                <div className={"d-none d-md-flex me-3"}>
                                    <div className={"home-button pointer text-center"} onClick={goToMain}>
                                        <i className={"fad fa-home fa-2x"} />
                                        <div>
                                            <OwLabel className={"pointer"} title={"home"} />
                                        </div>
                                    </div>
                                </div>
                                {props.categories.length && !hideCatalogBrowsing ? (
                                    <div className={"d-none d-md-flex flex-column catalog-button"} onClick={props.toggleCatalogSidebar}>
                                        <i className={"fad fa-book-open fa-2x"} />
                                        <div>
                                            <OwLabel title={"categories"} noBottomMargin={true} />
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        </>
                    )}
                </div>

                <div className={"search"}>
                    {!props.isReadOnly && !hideCatalogBrowsing ? (
                        <InputGroup>
                            <EcommerceCategorySearchSelection categories={props.categories} setCategory={setCategory} />
                            <OwInputWrapper name={"searchTerm"} onKeyDownFn={enterSearch} fn={handleChange} value={searchTerm} />
                            {quickSearchItems.length ? (
                                <SplitButton
                                    id={"search-split-btn"}
                                    variant={"primary"}
                                    title={<i className={"fas fa-search"} onClick={() => performSearch("ESSH - search onClick")} />}>
                                    <Accordion flush={true}>
                                        {quickSearchItems.map((item) => {
                                            return (
                                                <Accordion.Item eventKey={item.title} key={item.title}>
                                                    <Accordion.Header>{item.title}</Accordion.Header>
                                                    <Accordion.Body bsPrefix={"search-accordion-body"}>
                                                        {item.items.map((term) => {
                                                            return (
                                                                <Dropdown.Item
                                                                    key={term.value}
                                                                    onClick={() => handleQuickSearch(term.value)}>
                                                                    {term.value}
                                                                </Dropdown.Item>
                                                            );
                                                        })}
                                                    </Accordion.Body>
                                                </Accordion.Item>
                                            );
                                        })}
                                    </Accordion>
                                </SplitButton>
                            ) : (
                                <Button variant={"primary"} onClick={() => performSearch("ESSH - search onClick")}>
                                    <i className={"fas fa-search"} />
                                </Button>
                            )}
                            {/*<button*/}
                            {/*    className={"btn btn-sm btn-primary input-group-append"}*/}
                            {/*    onClick={() => {*/}
                            {/*        performSearch("ESSH - search onClick");*/}
                            {/*    }}>*/}
                            {/*    <i className={"fas fa-search"} />*/}
                            {/*</button>*/}
                        </InputGroup>
                    ) : null}
                </div>

                <div className={"cart-detail d-none d-md-flex"}>
                    {props.isCheckoutView ? null : (
                        <>
                            <OwLabel className={"cart"} title={"cart"} leftIconClass={"fad fa-shopping-cart"} fn={handleCartClick} />
                            <div className={"cart-count-wrapper"}>
                                <div className={"cart-count"}>{cartCount || props.order?.Order_Items.length || 0}</div>
                            </div>
                            {inactiveCartCount > 0 ? (
                                <div className={"inactive-cart-badge"} title={"Carts awaiting payment"} onClick={goToInactiveCarts}>
                                    <i className={"fas fa-exclamation-triangle me-1"} />
                                    {inactiveCartCount}
                                </div>
                            ) : null}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};
